import moment from "moment-timezone";
import queryString from "query-string";
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router";
import { RootState } from "..";
import { TAge } from "../components/MenuSearch/MenuSearch";
import { getPathWithoutBasename, getWindowR360 } from "../Helper";
import { requestLoadingByResvTypeSelector } from "../Selectors";
import { resvTypeByLid, userSelector } from "../selectors/Selectors";
import { fetchReservationTypeResults } from "../store/actions/reservationResult";
import { TReservationType } from "../store/types";
import useAppDispatch from "./useAppDispatch";
import useAppSelector from "./useAppSelector";
import useMemoizeArg from "./useMemoizeArg";
import { usePreFetch } from "./usePreFetch";

type TLocationState = {
    resetSearch?: boolean;
    resetPromoCode?: boolean;
};

const useChangeSearch = ({
    reservationResultSearch,
    reservationTypeId,
    grouppoollid,
    skipGuestsOnSearchWithPrice,
    allowSearchWithoutPrice,
    filterOnDays = true,
    disabled = false,
    forcePromotionCodeReset = false,
    usePromoCodeFromParams = true,
}: any) => {
    const location = useLocation();
    const navigate = useNavigate();
    const clientData = useAppSelector((state: RootState) => state.clientData);
    const guestData = clientData?.options?.general;
    const filter = useAppSelector(state => state.filter);
    const dispatch = useAppDispatch();
    const activeReservationtype = clientData.reservationtypes.find(
        (x: TReservationType) => x.id === parseInt(reservationTypeId)
    );
    const { useAutoSearch, autoSearchFromDate, autoSearchToDate } = (
        activeReservationtype ? activeReservationtype : {}
    ) as TReservationType;
    const isLoggedIn = !!useAppSelector(userSelector);
    const reservationType = useAppSelector((state: RootState) => resvTypeByLid(state, reservationTypeId));
    const { allowPreFetch, preFetchResult, preFetchLoading } = usePreFetch(reservationTypeId);
    const lastLocation = useRef<string>();
    const lastPreFetchUrl = useRef<string>();
    const lastSearchedPromoCode = useRef<{ [key: number]: string }>();
    const promoCode = filter.resvTypePromoCodes?.[reservationTypeId]?.code;
    const hasNewPromoCode = promoCode !== lastSearchedPromoCode.current?.[reservationTypeId];
    const fetchingReservationResult = useAppSelector(state =>
        requestLoadingByResvTypeSelector(state, "fetchReservationTypeResults", reservationTypeId)
    );
    const locationState = location.state as TLocationState;

    // Remove promotionCode from the query parameters.
    const removePromoCode = () => {
        const urlSearchParams = new URLSearchParams(location.search);
        urlSearchParams.delete("promotionCode");
        const params = Object.fromEntries(urlSearchParams.entries());

        navigate(`${location.pathname}?${queryString.stringify(params)}`, {
            replace: true,
            state: { resetPromoCode: false },
        });
    };

    useEffect(() => {
        if (disabled || !reservationType) return;

        let url;

        // Do a preFetch if only the url is provided in the preFetchResult, meaning it's new.
        const doPreFetchOnly = allowPreFetch && !!preFetchResult?.url && !preFetchResult?.search;

        if (doPreFetchOnly) {
            // To prevent multiple preFetches of the same url, we check if the url is the same as the last preFetch url.
            if (preFetchResult?.url === lastPreFetchUrl.current) {
                return;
            }

            if (fetchingReservationResult) {
                return;
            }

            lastPreFetchUrl.current = preFetchResult?.url;
            const preFetchUrl = new URL(preFetchResult.url, getWindowR360().__BASE_URL__);
            url = preFetchUrl.search;
        } else {
            const loginStateChangedSinceLastSearch =
                reservationResultSearch?.isLoggedIn !== isLoggedIn && reservationResultSearch?.isLoggedIn !== undefined;

            // Reset the last preFetch url if we're not doing a preFetch.
            lastPreFetchUrl.current = undefined;

            /* To prevent a search triggering when the preFetch is done (preFetchInProgress will turn false and trigger the useEffect),
               we check if the url is the same as before. */
            if (
                window.location.href === lastLocation.current &&
                !hasNewPromoCode &&
                !loginStateChangedSinceLastSearch
            ) {
                return;
            }

            lastLocation.current = window.location.href;
            url = location.search;
            lastSearchedPromoCode.current = { ...lastSearchedPromoCode.current, [reservationTypeId]: promoCode };
        }

        const urlSearchParams = new URLSearchParams(url);
        const params = Object.fromEntries(urlSearchParams.entries());

        let checkPriceAndAvailability = true;
        let startDate;
        let endDate;
        let guestArray = [];
        const requiredAges: { [key: string]: string[] } = {};

        if (!skipGuestsOnSearchWithPrice) {
            const obj = guestData?.ages && JSON.parse(guestData?.ages);
            const ages = obj ? obj[reservationTypeId] : [];

            Object.keys(params)
                .filter(key => key.startsWith("ages-for-"))
                .forEach(key => {
                    requiredAges[key.replace("ages-for-", "")] = params[key].split("-");
                });

            guestArray = ages
                ?.filter((age: TAge) => parseInt(params[age.key], 10) > 0)
                .map((age: TAge) =>
                    [...Array(parseInt(params[age.key], 10))].map((_e, i) => {
                        return age.requiresAge && requiredAges[age.key]
                            ? parseInt(requiredAges[age.key][i])
                            : age.value;
                    })
                )
                .flat();

            // If no guests are provided in the params, try to find guests from the previous search result.
            if (!guestArray?.length) {
                guestArray = reservationResultSearch?.guests || [];
            }
        }

        if (
            !guestArray.length &&
            !skipGuestsOnSearchWithPrice &&
            allowSearchWithoutPrice &&
            (!reservationResultSearch || reservationResultSearch?.checkPriceAndAvailability === false)
        ) {
            // If no search exists and no search params are provided, search for everything in the reservation type
            // without checking price and availablity.
            checkPriceAndAvailability = false;
            startDate = moment();
            endDate = moment().add(1, "year");
        } else if (
            skipGuestsOnSearchWithPrice &&
            allowSearchWithoutPrice &&
            !params.startDate &&
            (!reservationResultSearch || reservationResultSearch?.checkPriceAndAvailability === false)
        ) {
            checkPriceAndAvailability = false;
            startDate = moment();
            endDate = moment().add(1, "year");
        } else if (useAutoSearch) {
            // If autoSearchFromDate and autoSearchToDate is provided, we always expect the autoSearchToDate to be later than autoSearchFromDate.
            startDate = moment().isAfter(moment(autoSearchFromDate)) ? moment() : moment(autoSearchFromDate);
            endDate = startDate.isBefore(moment(autoSearchToDate)) ? moment(autoSearchToDate) : moment().add(1, "year");
        } else if (
            (params.startDate || reservationResultSearch?.arrdate) &&
            (params.endDate || reservationResultSearch?.depdate)
        ) {
            startDate = moment(params.startDate || reservationResultSearch?.arrdate);
            endDate = moment(params.endDate || reservationResultSearch?.depdate);

            if (skipGuestsOnSearchWithPrice === false && guestArray.length === 0) {
                console.log(
                    "ChangeSearch - useEffect[location.search] - Early return for no guests on search with price"
                );
                return;
            }
        } else {
            return;
        }

        if (!startDate?.isValid?.() || !endDate?.isValid?.()) {
            return;
        }

        const requestData = {
            resvtype: reservationTypeId,
            oldStartDate: reservationResultSearch?.arrdate,
            oldEndDate: reservationResultSearch?.depdate,
            startdate: startDate.format("YYYY-MM-DD"),
            enddate: endDate.format("YYYY-MM-DD"),
            oldAges: reservationResultSearch?.guests || [],
            ages: guestArray,
            oldPromoCode: reservationResultSearch?.promoCode || "",
            promoCode: "",
            oldGrouppoollid: "",
            grouppoollid: "",
            oldPricecode: "",
            pricecode: "",
            filterOnDays,
            oldIsLoggedIn: reservationResultSearch?.isLoggedIn,
            isLoggedIn,
            useAutoSearch,
            doPreFetchOnly,
            preFetchResult,
        };

        const getPromoCode = () => {
            if (forcePromotionCodeReset) {
                return "";
            }

            // If a promo code is provided in the search params, use it.
            if (usePromoCodeFromParams && params.promotionCode) {
                return params.promotionCode;
            }

            // If we're resetting the promo code, return an empty string.
            if (locationState?.resetPromoCode) {
                return "";
            }

            // If no promo code is provided in the search params, return the promo code from the previous search result if any.
            if (reservationResultSearch?.promoCode) {
                return reservationResultSearch?.promoCode;
            }

            return "";
        };

        // Only search using promo code, groupoollid and pricecode when searching with price.
        if (checkPriceAndAvailability) {
            requestData.oldGrouppoollid = reservationResultSearch?.grouppoollid || "";
            requestData.grouppoollid = grouppoollid || "";
            requestData.oldPricecode = reservationResultSearch?.pricecode || "";
            requestData.pricecode = params.pricecode || "";
            requestData.promoCode = getPromoCode();
        }

        dispatch(fetchReservationTypeResults(requestData, checkPriceAndAvailability, false, removePromoCode));
        // eslint-disable-next-line
    }, [
        dispatch,
        location.search,
        locationState?.resetSearch,
        useMemoizeArg(preFetchResult),
        preFetchLoading,
        reservationTypeId,
        useMemoizeArg(reservationResultSearch),
        grouppoollid,
        useMemoizeArg(guestData?.ages),
        skipGuestsOnSearchWithPrice,
        allowSearchWithoutPrice,
        isLoggedIn,
        useAutoSearch,
        forcePromotionCodeReset,
        usePromoCodeFromParams,
    ]);
};

export default useChangeSearch;
